import React, { memo, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { Button } from 'components/ui/Button';
import { EyeIcon } from 'components/icons/Icons';
import { DocumentCardTooltip } from './DocumentCardTooltip';
import GenericBadge from 'components/ui/Badge';
import { GenericColors } from 'enums/genericColors';
import { Sizes } from 'components/ui/Loader';
import { SimulationDocumentDto } from 'api/documents';
import { GenericLoading } from 'components/ui/Loader';
import { format } from 'date-fns';
import { useDocumentViewerDialog } from 'components/domain/Document/useDocumentViewerDialog';

type EmailRecipient = {
  email: string;
  name?: string;
};

type DocumentCardItemProps = {
  simulationDoc: SimulationDocumentDto;
  isLoading?: boolean;
  /** Optional email config for borrower documents */
  emailConfig?: {
    risqueAssureId?: string;
    sendTo?: EmailRecipient[];
  };
};

export const DocumentCardItem: React.FC<DocumentCardItemProps> = memo(({
  simulationDoc,
  isLoading = false,
  emailConfig
}) => {
  const { t } = useTranslation(NsKey.Contractualization);
  const { openDocumentViewer } = useDocumentViewerDialog();

  const visualiseDocument = async () => {
    if (simulationDoc?.storedFileId) {
      // Only pass emailConfig if risqueAssureId is defined (required by EmailConfig type)
      const fullEmailConfig = emailConfig?.risqueAssureId ? {
        storedFileId: simulationDoc.storedFileId,
        risqueAssureId: emailConfig.risqueAssureId,
        sendTo: emailConfig.sendTo,
        fileTypeInt: simulationDoc.fileType,
        fileName: 'Devis_MNCAP.pdf'
      } : undefined;

      openDocumentViewer({
        title: simulationDoc.label || 'Document',
        storedFileId: simulationDoc.storedFileId,
        emailConfig: fullEmailConfig
      });
    }
  };

  return (
    <div className='flex items-center p-4 border rounded-xl bg-white'>
      <div className='grow flex gap-4'>
        <GenericBadge color={GenericColors.Primary} className="font-bold" label={'PDF'} badgeType='sm-bold' />
        <div>
          <p className='font text-primary'>{simulationDoc.label}</p>
          <p className='text-sm text-primary'>
            {
              simulationDoc.signatureDate &&
              `${t('CONTRACTUALIZATION.LABELS.SIGNED_AT')} ${format(new Date(simulationDoc.signatureDate), "dd MMM yyyy HH:mm")}`
            }
          </p>
        </div>
      </div>
      {
        (isLoading || !simulationDoc.storedFileId) &&
        <div className='flex items-center justify-center gap-2'>
          <GenericLoading size={Sizes.sm} loading />
        </div>
      }
      {
        !isLoading && simulationDoc.storedFileId &&
        <div className='relative flex items-center gap-2'>
          <Button
            color='slate'
            round
            onClick={(e: MouseEvent) => { e.stopPropagation(); visualiseDocument(); }}
          >
            <EyeIcon />
          </Button>
          <DocumentCardTooltip simulationDoc={simulationDoc} />
        </div>
      }
    </div>
  );
});

DocumentCardItem.displayName = 'DocumentCardItem';
